import React from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';

import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import Divider from '@material-ui/core/Divider';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import MicIcon from '@material-ui/icons/Mic';

import Banner from '~/components/Banner';
import { observer } from 'mobx-react';
import authStore from '~/stores/authStore';
import TablePrice from '~/components/Admin/TablePrice';
import { WTM_PROMO_ACTIVE } from '~/components/Admin/Calculator';

const useStyles = makeStyles(({ palette, spacing }) =>
    createStyles({
        text: {
            color: palette.background.paper,
            textShadow: `1px 1px 1px ${palette.primary.dark}`
        },
        root: {
            margin: spacing(1, 0),
            display: 'flex',
            flexDirection: 'column',
            '& > *': {
                marginTop: spacing(3)
            },
            '& li': {
                margin: spacing(1, 0)
            },
            maxWidth: '100%',
            overflowX: 'auto'
        }
    })
);

const langLib = {
    en: {
        header: 'Price',
        contactUs: 'Select your feet pit price',
        title: "We're all ears. Price (30 days), $",
        speechTitle: 'Call Transcription API',
        speechSubtitle: 'Pay per minute of audio — no monthly fees.',
        speechFormat: 'Format',
        speechFormats: 'MP3, WAV, OGG, M4A and any other',
        speechLang: 'Languages',
        speechLangs: 'Russian, English',
        speechPrice: 'Price per minute',
        speechPriceValue: '$0.003',
        speechBilling: 'Billing',
        speechBillingValue: 'Balance top-up, pay-as-you-go',
        promoNote: 'WA + TG + MAX bundle at single-messenger price \u2014 on orders from 100 numbers'
    },
    ru: {
        header: 'Тарифы',
        contactUs: 'Стоимость',
        title: 'Выберите подходящий для вас тариф, за номер (30 дней), руб.',
        speechTitle: 'API расшифровки звонков',
        speechSubtitle: 'Поминутная оплата — без абонентской платы.',
        speechFormat: 'Форматы файлов',
        speechFormats: 'MP3, WAV, OGG, M4A и любые другие',
        speechLang: 'Языки',
        speechLangs: 'Русский, Английский',
        speechPrice: 'Стоимость минуты',
        speechPriceValue: '0,19 ₽',
        speechBilling: 'Способ оплаты',
        speechBillingValue: 'Пополнение баланса, pay-as-you-go',
        promoNote: 'WA + TG + MAX по цене одного мессенджера \u2014 при заказе от 100 номеров'
    }
} as const;

const SDK = () => {
    const { lang } = authStore;
    const lib = langLib[lang];
    const classes = useStyles();

    return (
        <>
            <Banner style={{ height: '160px' }}>
                <Typography variant="h3" component="h1" className={classes.text} align="center">
                    {lib['header']}
                </Typography>
            </Banner>
            <Container maxWidth="md" style={{ paddingBottom: '3rem' }}>
                <div className={classes.root}>
                    <Typography variant="h4">{lib['contactUs']}</Typography>
                    <Typography variant="h5">{lib['title']}</Typography>
                    {WTM_PROMO_ACTIVE && (
                        <Typography
                            variant="subtitle1"
                            style={{
                                background: 'linear-gradient(90deg, #25D366, #0088cc, #733ce4)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontWeight: 700,
                                marginTop: 8
                            }}
                        >
                            {lib['promoNote']}
                        </Typography>
                    )}
                    <TablePrice extended />

                    <Divider />

                    <Typography variant="h4" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MicIcon fontSize="large" color="secondary" />
                        {lib['speechTitle']}
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary">
                        {lib['speechSubtitle']}
                    </Typography>
                    <Table size="medium">
                        <TableHead>
                            <TableRow>
                                <TableCell>{lib['speechFormat']}</TableCell>
                                <TableCell align="right">{lib['speechLang']}</TableCell>
                                <TableCell align="right">{lib['speechPrice']}</TableCell>
                                <TableCell align="right">{lib['speechBilling']}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>{lib['speechFormats']}</TableCell>
                                <TableCell align="right">{lib['speechLangs']}</TableCell>
                                <TableCell align="right">
                                    <b>{lib['speechPriceValue']}</b>
                                </TableCell>
                                <TableCell align="right">{lib['speechBillingValue']}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </Container>
        </>
    );
};

export default observer(SDK);
